import { useEffect, useState } from 'react'
import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Button from '@mui/joy/Button'
import IconButton from '@mui/joy/IconButton'
import Tabs from '@mui/joy/Tabs'
import TabList from '@mui/joy/TabList'
import Tab from '@mui/joy/Tab'
import TabPanel from '@mui/joy/TabPanel'
import Table from '@mui/joy/Table'
import Sheet from '@mui/joy/Sheet'
import Alert from '@mui/joy/Alert'
import CircularProgress from '@mui/joy/CircularProgress'
import Divider from '@mui/joy/Divider'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import BlockIcon from '@mui/icons-material/Block'
import StopCircleIcon from '@mui/icons-material/StopCircle'

import { getCallAudioUrl, stopCall, timestampToDate } from '../../api/dsGmeetApi'
import { useDsGmeetBot } from '../../hooks/useDsGmeetBot'
import { useCurrentUserId } from '../../hooks/useCurrentUserId'
import { TranscriptViewer } from './TranscriptViewer'
import { StatusPill } from './StatusPill'

interface Props {
    botId: string
    onClose: () => void
    onChanged?: () => void
}

/**
 * Detail panel for a single bot. Used inside QueueTab as the right-side pane.
 * Tabs: Overview / Transcript / Activity. Audio player on Overview.
 */
export function CallDetail({ botId, onClose, onChanged }: Props) {
    const currentUserId = useCurrentUserId()
    const { bot, activity, transcript, loading, error, refetch, requeue, skip } = useDsGmeetBot(botId, currentUserId)
    const [tab, setTab] = useState<number>(0)
    const [audioUrl, setAudioUrl] = useState<string | null>(null)
    const [audioError, setAudioError] = useState('')
    const [actionError, setActionError] = useState('')
    const [actionPending, setActionPending] = useState(false)

    useEffect(() => {
        setAudioUrl(null)
        setAudioError('')
        if (!bot?.gcs_audio_uri) return
        let cancelled = false
        getCallAudioUrl(bot.recall_bot_id)
            .then((url) => {
                if (!cancelled) setAudioUrl(url)
            })
            .catch((err) => !cancelled && setAudioError(err instanceof Error ? err.message : String(err)))
        return () => {
            cancelled = true
        }
    }, [bot])

    return (
        <Stack sx={{ height: '100%', minHeight: 0 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.level1' }}
            >
                <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    <Typography level="title-sm">Call detail</Typography>
                    {bot && <StatusPill status={bot.status} />}
                    {bot && (
                        <Chip size="sm" variant="outlined">
                            {bot.trigger_source}
                        </Chip>
                    )}
                </Stack>
                <Stack direction="row" gap={0.5}>
                    <IconButton size="sm" variant="plain" onClick={refetch} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                    <IconButton size="sm" variant="plain" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </Stack>

            {loading && !bot && (
                <Stack alignItems="center" sx={{ p: 3 }}>
                    <CircularProgress size="sm" />
                </Stack>
            )}
            {error && (
                <Alert color="danger" sx={{ m: 2 }}>
                    {error}
                </Alert>
            )}

            {bot && (
                <>
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: 'text.tertiary' }}>
                            {bot.recall_bot_id}
                        </Typography>
                    </Box>

                    <Tabs value={tab} onChange={(_, v) => setTab(v as number)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <TabList sx={{ px: 1.5 }}>
                            <Tab value={0}>Overview</Tab>
                            <Tab value={1}>Transcript</Tab>
                            <Tab value={2}>Activity</Tab>
                        </TabList>

                        <TabPanel value={0} sx={{ overflowY: 'auto', minHeight: 0 }}>
                            <Stack gap={1.25}>
                                <KV label="Meeting URL" value={bot.meeting_url || '—'} mono />
                                <KV label="Calendar event" value={bot.recall_calendar_event_id ?? '—'} mono />
                                <KV label="GCS audio" value={bot.gcs_audio_uri ?? '—'} mono />
                                <KV label="Ack outcome" value={bot.ack_outcome ?? '—'} />
                                {bot.ack_error && <KV label="Ack error" value={bot.ack_error} color="danger" />}
                                <KV label="Created" value={timestampToDate(bot.created_at)?.toLocaleString() ?? '—'} />
                                {bot.acked_at && <KV label="Acked at" value={timestampToDate(bot.acked_at)?.toLocaleString() ?? '—'} />}

                                {audioError && (
                                    <Alert color="warning" size="sm">
                                        Audio not playable: {audioError}
                                    </Alert>
                                )}
                                {audioUrl && (
                                    <Sheet variant="soft" sx={{ borderRadius: 'sm', p: 1.5 }}>
                                        <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>
                                            Recording (signed URL, 10-minute TTL)
                                        </Typography>
                                        <audio controls src={audioUrl} style={{ width: '100%' }} />
                                    </Sheet>
                                )}

                                <Divider />

                                {actionError && (
                                    <Alert color="danger" size="sm" onClose={() => setActionError('')}>
                                        {actionError}
                                    </Alert>
                                )}

                                <Stack direction="row" gap={1} flexWrap="wrap">
                                    {(bot.status === 'CREATED' || bot.status === 'RECORDING') && (
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            color="danger"
                                            startDecorator={<StopCircleIcon />}
                                            loading={actionPending}
                                            onClick={async () => {
                                                setActionError('')
                                                setActionPending(true)
                                                try {
                                                    await stopCall(bot.recall_bot_id)
                                                    await refetch()
                                                    onChanged?.()
                                                } catch (err) {
                                                    setActionError(err instanceof Error ? err.message : String(err))
                                                } finally {
                                                    setActionPending(false)
                                                }
                                            }}
                                        >
                                            Stop bot
                                        </Button>
                                    )}
                                    {(bot.status === 'PROCESSED' || bot.status === 'UNATTRIBUTED' || bot.status === 'FAILED') && (
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            color="warning"
                                            startDecorator={<ReplayIcon />}
                                            loading={actionPending}
                                            onClick={async () => {
                                                setActionError('')
                                                setActionPending(true)
                                                try {
                                                    await requeue()
                                                    onChanged?.()
                                                } catch (err) {
                                                    setActionError(err instanceof Error ? err.message : String(err))
                                                } finally {
                                                    setActionPending(false)
                                                }
                                            }}
                                        >
                                            Re-queue for processing
                                        </Button>
                                    )}
                                    {bot.status === 'PENDING' && (
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            color="danger"
                                            startDecorator={<BlockIcon />}
                                            loading={actionPending}
                                            onClick={async () => {
                                                setActionError('')
                                                setActionPending(true)
                                                try {
                                                    await skip()
                                                    onChanged?.()
                                                } catch (err) {
                                                    setActionError(err instanceof Error ? err.message : String(err))
                                                } finally {
                                                    setActionPending(false)
                                                }
                                            }}
                                        >
                                            Mark do-not-process
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        </TabPanel>

                        <TabPanel value={1} sx={{ overflowY: 'auto', minHeight: 0 }}>
                            {transcript ? (
                                transcript.length > 0 ? (
                                    <TranscriptViewer segments={transcript} />
                                ) : (
                                    <Typography level="body-sm">Empty transcript.</Typography>
                                )
                            ) : (
                                <Alert color="warning" size="sm">
                                    Transcript not ready yet. Recall produces it asynchronously after the recording lands.
                                </Alert>
                            )}
                        </TabPanel>

                        <TabPanel value={2} sx={{ overflowY: 'auto', minHeight: 0, p: 0 }}>
                            <Table size="sm" stickyHeader>
                                <thead>
                                    <tr>
                                        <th>Event</th>
                                        <th style={{ width: 110 }}>Status</th>
                                        <th style={{ width: 90 }}>Retries</th>
                                        <th style={{ width: 150 }}>Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activity.map((j) => (
                                        <tr key={j.event_type}>
                                            <td>
                                                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                    {j.event_type}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Chip
                                                    size="sm"
                                                    color={j.status === 'COMPLETED' ? 'success' : j.status === 'FAILED' ? 'danger' : 'neutral'}
                                                    variant="soft"
                                                >
                                                    {j.status}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{j.retry_count}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs" color="neutral">
                                                    {timestampToDate(j.updated_at)?.toLocaleString()}
                                                </Typography>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {activity.length === 0 && (
                                <Typography level="body-sm" color="neutral" sx={{ p: 2 }}>
                                    No activity yet.
                                </Typography>
                            )}
                        </TabPanel>
                    </Tabs>
                </>
            )}
        </Stack>
    )
}

function KV({ label, value, color, mono }: { label: string; value: string; color?: 'danger'; mono?: boolean }) {
    return (
        <Stack direction="row" gap={1.5} alignItems="flex-start">
            <Typography level="body-xs" sx={{ minWidth: 130, color: 'text.tertiary' }}>
                {label}
            </Typography>
            <Typography
                level="body-sm"
                sx={{
                    flex: 1,
                    wordBreak: 'break-all',
                    fontFamily: mono ? 'monospace' : undefined,
                    color: color === 'danger' ? 'danger.500' : undefined,
                }}
            >
                {value}
            </Typography>
        </Stack>
    )
}
